

import i18next from 'i18next';
import { sha256 } from 'js-sha256';
import moment from 'moment';

import {
    createRecordingEvent,
    sendAnalytics
} from '../analytics';
import logger from '../app/logger';
import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../base/app';
import { CONFERENCE_WILL_JOIN, CONFERENCE_WILL_LEAVE, getCurrentConference } from '../base/conference';
import { apiKEY } from '../base/config/constants';
import MeetHourJS, {
    MHConferenceEvents,
    MHRecordingConstants
} from '../base/lib-meet-hour';
import { IS_RECORDING_AND_LIVESTREAMING_ON, fetchAxiosApi, recordingandLivestreamingOn } from '../base/mtApi';
import { ULR_PATHS } from '../base/mtApi/constants';
import {
    getParticipantDisplayName,
    hideRecordingConsentModal,
    isLocalParticipantModerator,
    resetRecordingStartedByUser,
    showRecordingConsentModal,
    updateLocalRecordingStatus
} from '../base/participants';
import { USER_WAITING_REGISTER, USER_WAITING_SOUND } from '../base/premeeting/components/web/constants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import {
    playSound,
    registerSound,
    stopSound,
    unregisterSound
} from '../base/sounds';
import { getBackendSafeRoomName } from '../base/util';
import { checkIfNotReactNative, isDomainWeborNative } from '../base/util/checkOS';
import {
    NOTIFICATION_TIMEOUT_TYPE,
    NOTIFICATION_TYPE,
    showErrorNotification,
    showNotification,
    showWarningNotification
} from '../notifications';
import { setRequestingSubtitles } from '../subtitles/actions';
import { isRecorderTranscriptionsRunning } from '../transcribing/functions';

import {
    RECORDING_SESSION_UPDATED,
    RECORDING_STOP_CLEARED,
    RECORDING_STOP_REQUESTED,
    START_LOCAL_RECORDING,
    STOP_LOCAL_RECORDING
} from './actionTypes';
import {
    clearRecordingSessions,
    hidePendingRecordingNotification,
    showPendingRecordingNotification,
    showRecordingError,
    showRecordingLimitNotification,
    showStartedRecordingNotification,
    showStoppedRecordingNotification,
    updateRecordingSessionData
} from './actions';
import { setRecordingMode } from './actions.any';
import LocalRecordingManager from './components/Recording/LocalRecordingManager';
import {
    LIVE_STREAMING_OFF_SOUND_ID,
    LIVE_STREAMING_ON_SOUND_ID,
    RECORDING_OFF_SOUND_ID,
    RECORDING_ON_SOUND_ID
} from './constants';
import { getResourceId, getSessionById } from './functions';
import {
    LIVE_STREAMING_OFF_SOUND_FILE,
    LIVE_STREAMING_ON_SOUND_FILE,
    RECORDING_OFF_SOUND_FILE,
    RECORDING_ON_SOUND_FILE
} from './sounds';


/**
 * StateListenerRegistry provides a reliable way to detect the leaving of a
 * conference, where we need to clean up the recording sessions.
 */
StateListenerRegistry.register(
    /* selector */ state => getCurrentConference(state),
    /* listener */ (conference, { dispatch }) => {
        if (!conference) {
            dispatch(clearRecordingSessions());
        }
    }
);

/**
 * Stored handler for RECORDER_STATE_CHANGED so it can be removed on conference leave.
 */
let _onRecorderStateChanged = null;

/**
 * The conference the recording listeners above are currently attached to, plus the command handler,
 * so both can be detached again. CONFERENCE_WILL_LEAVE only carries the LEAVING conference, which is
 * not necessarily the one we attached to.
 */
let _recordingHandlers = null;

/**
 * Detaches the recording listeners registered by a previous CONFERENCE_WILL_JOIN, if any.
 *
 * CONFERENCE_WILL_JOIN is not dispatched once per conference - `startKnocking()` re-dispatches it on
 * every lobby knock and `checkIfCanJoin()` on every authentication retry, both against the SAME
 * conference object. Without detaching first, each pass added another RECORDER_STATE_CHANGED listener
 * (EventEmitter, so it warns once past ten) and another 'recording-stop-requested' presence handler.
 * The presence handler is worse: addPresenceListener dedups by function identity, and each pass builds
 * a fresh arrow function, so it accumulates silently with no warning at all and every stop request is
 * then handled N times.
 *
 * @param {Object} [conference] - Conference to detach from; defaults to the one we attached to.
 * @returns {void}
 */
function _detachRecordingHandlers(conference) {
    if (!_recordingHandlers) {
        return;
    }

    const c = conference || _recordingHandlers.conference;

    if (c) {
        c.off(MHConferenceEvents.RECORDER_STATE_CHANGED, _recordingHandlers.onRecorderStateChanged);
        c.removeCommandListener('recording-stop-requested', _recordingHandlers.onRecordingStopRequested);
    }

    _recordingHandlers = null;
    _onRecorderStateChanged = null;
}

/**
 * Timers to automatically discard recording sessions stuck in PENDING after 5 minutes.
 */
const pendingTimers = {};

/**
 * The redux middleware to handle the recorder updates in a React way.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    let oldSessionData;

    if (action.type === RECORDING_SESSION_UPDATED) {
        oldSessionData
            = getSessionById(getState(), action.sessionData.id);
    }

    const result = next(action);

    switch (action.type) {
    case APP_WILL_MOUNT:
        if (checkIfNotReactNative()) {
            dispatch(registerSound(
                LIVE_STREAMING_OFF_SOUND_ID,
                LIVE_STREAMING_OFF_SOUND_FILE));

            dispatch(registerSound(
                LIVE_STREAMING_ON_SOUND_ID,
                LIVE_STREAMING_ON_SOUND_FILE));

            dispatch(registerSound(
                    RECORDING_ON_SOUND_ID,
                    RECORDING_ON_SOUND_FILE));

            dispatch(registerSound(
                RECORDING_OFF_SOUND_ID,
                RECORDING_OFF_SOUND_FILE));

            dispatch(registerSound(USER_WAITING_REGISTER, USER_WAITING_SOUND, { loop: true }));
        }


        break;

    case APP_WILL_UNMOUNT:
        dispatch(unregisterSound(LIVE_STREAMING_OFF_SOUND_ID));
        dispatch(unregisterSound(LIVE_STREAMING_ON_SOUND_ID));
        dispatch(unregisterSound(RECORDING_OFF_SOUND_ID));
        dispatch(unregisterSound(RECORDING_ON_SOUND_ID));

        break;

    case CONFERENCE_WILL_JOIN: {
        const { conference } = action;

        // Clear any leftover recording sessions from a previous conference
        // (e.g. when a participant leaves and rejoins). The StateListenerRegistry
        // below also clears sessions when getCurrentConference becomes null, but
        // that can race with the incoming XMPP presence from the new conference.
        // Clearing here — before any presence arrives — ensures the rejoining
        // participant never sees a stale recording icon from a session the
        // organizer already stopped.
        //
        // Stays unconditional and ahead of the guard below: it depends on nothing but the store, and
        // skipping it would reintroduce exactly the stale-icon bug it exists to prevent.
        dispatch(clearRecordingSessions());

        if (!conference) {
            break;
        }

        // Idempotent: see _detachRecordingHandlers. This action can arrive repeatedly for the same
        // conference, and a middleware cannot control who dispatches to it.
        _detachRecordingHandlers();

        _onRecorderStateChanged = recorderSession => {
            if (recorderSession) {
                recorderSession.getID()
                    && dispatch(
                        updateRecordingSessionData(recorderSession));

                const state = getState();
                const isSwitchingBreakoutRoom = state['features/base/mtApi']?.isSwitchingBreakOutRoom;
                const isSwitchingToMainRoom = state['features/base/mtApi']?.isSwitchingToMainRoom;
                const isSwitchingRoom = isSwitchingBreakoutRoom || isSwitchingToMainRoom;

                if (recorderSession.getError() && !isSwitchingRoom) {
                    _showRecordingErrorNotification(
                        recorderSession, dispatch, NOTIFICATION_TIMEOUT_TYPE.STICKY);
                }
            }

            return;
        };

        const onRecordingStopRequested = ({ value }) => {
            dispatch({
                type: RECORDING_STOP_REQUESTED,
                sessionId: value
            });
        };

        conference.on(MHConferenceEvents.RECORDER_STATE_CHANGED, _onRecorderStateChanged);
        conference.addCommandListener('recording-stop-requested', onRecordingStopRequested);

        // Keep both references so they can actually be removed later.
        _recordingHandlers = {
            conference,
            onRecorderStateChanged: _onRecorderStateChanged,
            onRecordingStopRequested
        };

        break;
    }

    case CONFERENCE_WILL_LEAVE: {
        const { conference: leavingConf } = action;

        _detachRecordingHandlers(leavingConf);

        // Clear and delete all pending timers on leaving conference
        Object.keys(pendingTimers).forEach(sessionId => {
            clearTimeout(pendingTimers[sessionId]);
            delete pendingTimers[sessionId];
        });

        break;
    }

    case START_LOCAL_RECORDING: {
        const { localRecording } = getState()['features/base/config'];
        const { onlySelf } = action;

        LocalRecordingManager.startLocalRecording({
            dispatch,
            getState
        }, action.onlySelf)
        .then(() => {
            const props = {
                descriptionKey: 'recording.on',
                titleKey: 'dialog.recording'
            };

            if (localRecording?.notifyAllParticipants && !onlySelf) {
                dispatch(playSound(RECORDING_ON_SOUND_ID));
            }
            dispatch(showNotification(props, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            dispatch(showNotification({
                titleKey: 'recording.localRecordingStartWarningTitle',
                descriptionKey: 'recording.localRecordingStartWarning'
            }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
            if (onlySelf && !LocalRecordingManager.selfRecording.withVideo) {
                // The recording's tracks are fixed when it starts: with the camera off it is audio only for good.
                dispatch(showNotification({
                    appearance: NOTIFICATION_TYPE.WARNING,
                    titleKey: 'recording.localRecordingAudioOnlyTitle',
                    descriptionKey: 'recording.localRecordingAudioOnly'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            if (LocalRecordingManager.shouldWarnAboutMissingParentDownloadBridge) {
                dispatch(showWarningNotification({
                    titleKey: 'recording.localRecordingStartWarningTitle',
                    descriptionKey: 'recording.localRecordingIframeDownloadBridgeWarning'
                }));
            }
            dispatch(updateLocalRecordingStatus(true, onlySelf));
            sendAnalytics(createRecordingEvent('started', `local${onlySelf ? '.self' : ''}`));
            if (typeof APP !== 'undefined') {
                APP.API.notifyRecordingStatusChanged(
                    true, 'local', undefined, isRecorderTranscriptionsRunning(getState()));
            }
        })
        .catch(err => {
            logger.error('Capture failed', err);

            let descriptionKey = 'recording.error';
            let description;
            const isCrossOriginSubFrameFilePickerError
                = err?.name === 'SecurityError'
                    && typeof err?.message === 'string'
                    && err.message.includes('Cross origin sub frames');

            if (err.message === 'WrongSurfaceSelected') {
                descriptionKey = 'recording.surfaceError';

            } else if (err.message === 'NoLocalStreams') {
                descriptionKey = 'recording.noStreams';
            } else if (err.message === 'NoMicTrack') {
                descriptionKey = 'recording.noMicPermission';
            } else if (isCrossOriginSubFrameFilePickerError) {
                descriptionKey = 'recording.localRecordingIframeCrossOriginBlocked';
            }
            const props = {
                descriptionKey,
                description,
                titleKey: 'recording.failedToStart'
            };

            if (typeof APP !== 'undefined') {
                APP.API.notifyRecordingStatusChanged(
                    false, 'local', err.message, isRecorderTranscriptionsRunning(getState()));
            }

            dispatch(showErrorNotification(props));
        });
        break;
    }

    case STOP_LOCAL_RECORDING: {
        const { localRecording } = getState()['features/base/config'];

        if (LocalRecordingManager.isRecordingLocally()) {
            LocalRecordingManager.stopLocalRecording();
            dispatch(updateLocalRecordingStatus(false));
            if (localRecording?.notifyAllParticipants && !LocalRecordingManager.selfRecording.on) {
                dispatch(playSound(RECORDING_OFF_SOUND_ID));
            }
            if (typeof APP !== 'undefined') {
                APP.API.notifyRecordingStatusChanged(
                    false, 'local', undefined, isRecorderTranscriptionsRunning(getState()));
            }
        }
        break;
    }

    case RECORDING_STOP_REQUESTED: {
        const sessionId = action.sessionId;

        if (pendingTimers[sessionId]) {
            clearTimeout(pendingTimers[sessionId]);
            delete pendingTimers[sessionId];
        }

        // Dismiss the "preparing to record" banner right away, so the UI does
        // not stay stuck while waiting for the server to confirm the stop.
        const stoppedSession = getSessionById(getState(), sessionId);

        if (stoppedSession && stoppedSession.mode) {
            dispatch(hidePendingRecordingNotification(stoppedSession.mode));
        }
        break;
    }

    case RECORDING_SESSION_UPDATED: {
        // When in recorder mode no notifications are shown
        // or extra sounds are also not desired
        // but we want to indicate those in case of sip gateway
        const {
            iAmRecorder,
            iAmSipGateway,
            disableRecordAudioNotification,
            transcription,
            recordingLimit
        } = getState()['features/base/config'];

        const conference = getCurrentConference(getState());

        let { isRecordingandLivestreamingOn = false } = getState()['features/base/mtApi'];

        const sessions = getState()['features/recording'].sessionDatas || [];
        const activeFileSession = sessions.find(s => s.mode === MHRecordingConstants.mode.FILE
            && (s.status === MHRecordingConstants.status.ON || s.status === MHRecordingConstants.status.PENDING));
        const activeStreamSession = sessions.find(s => s.mode === MHRecordingConstants.mode.STREAM
            && (s.status === MHRecordingConstants.status.ON || s.status === MHRecordingConstants.status.PENDING));

        const hasRecLiveSubSessions = sessions.some(s => {
            if (s.status !== MHRecordingConstants.status.ON && s.status !== MHRecordingConstants.status.PENDING) {
                return false;
            }
            const subs = s.subSessions || [];
            const hasRec = subs.some(sub => sub.target === 'recording' && sub.status === 'on');
            const hasStream = subs.some(sub => sub.target === 'stream' && sub.status === 'on');

            return hasRec && hasStream;
        });

        const isRecLiveActive = Boolean(
            hasRecLiveSubSessions
            || (activeFileSession && activeStreamSession)
            || (activeFileSession && typeof activeFileSession.liveStreamViewURL === 'string' && activeFileSession.liveStreamViewURL.trim() !== '')
        );

        if (isRecLiveActive && !isRecordingandLivestreamingOn) {
            isRecordingandLivestreamingOn = true;
            dispatch(recordingandLivestreamingOn(true));
        }

        const isTranscribing = getState()['features/transcribing']?.isTranscribing;

        let isRecorderButNotSipGateway = false;
        let isRecordAndTRANSCRIBER = false;

        if (iAmRecorder && isTranscribing) {
            isRecordAndTRANSCRIBER = true;
        }

        if (iAmRecorder && !iAmSipGateway) {
            isRecorderButNotSipGateway = true;
        }

        const updatedSessionData
            = getSessionById(getState(), action.sessionData.id);
        const { initiator, mode, terminator } = updatedSessionData;
        const { PENDING, OFF, ON } = MHRecordingConstants.status;
        const { stoppingSessionIds } = getState()['features/recording'];
        const isStopping = Boolean(stoppingSessionIds && typeof stoppingSessionIds.has === 'function' && stoppingSessionIds.has(updatedSessionData.id));

        if (!isRecorderButNotSipGateway) {
            const sessionId = updatedSessionData.id;

            // When a stop has been requested we want to keep retrying the stop
            // on every PENDING update, not only on the first transition into
            // PENDING, since Jibri may report PENDING repeatedly.
            if (updatedSessionData.status === PENDING
                    && (isStopping || !oldSessionData || oldSessionData.status !== PENDING)) {
                if (isStopping) {
                    const conf = getCurrentConference(getState());

                    conf && logger.info(`Retrying stopRecording for session ${sessionId} during PENDING state`);
                    conf && conf.stopRecording(sessionId);
                } else {
                    const startPendingTimer = () => {
                        dispatch(showPendingRecordingNotification(mode));

                        // Clear any existing timer for this session
                        if (pendingTimers[sessionId]) {
                            clearTimeout(pendingTimers[sessionId]);
                        }

                        // Start 5-minute timer to auto-discard pending status if it doesn't transition to ON
                        pendingTimers[sessionId] = setTimeout(() => {
                            delete pendingTimers[sessionId];

                            logger.warn(`Recording session ${sessionId} pending timeout. Discarding.`);

                            // 1. Hide the notification
                            dispatch(hidePendingRecordingNotification(mode));

                            // 2. Stop the recording session
                            const conf = getCurrentConference(getState());

                            if (conf) {
                                conf.stopRecording(sessionId);
                            }

                            // 3. Clear/remove the session from redux state (force transition to OFF)
                            dispatch({
                                type: RECORDING_SESSION_UPDATED,
                                sessionData: {
                                    id: sessionId,
                                    mode,
                                    status: MHRecordingConstants.status.OFF,
                                    error: 'timeout'
                                }
                            });
                        }, 5 * 60 * 1000); // 5 minutes
                    };

                    startPendingTimer();
                }

            } else if (updatedSessionData.status !== PENDING) {
                // Clear the timer if it transitions out of PENDING
                if (pendingTimers[sessionId]) {
                    clearTimeout(pendingTimers[sessionId]);
                    delete pendingTimers[sessionId];
                }

                dispatch(hidePendingRecordingNotification(mode));
            }
        }

        if (isRecordAndTRANSCRIBER) {
            const { confSettings } = getState()['features/base/jwt'];
            const _autoCaptionOnRecord = transcription?.autoCaptionOnRecord ?? false;
            const language = getState()['features/subtitles']._language;

            if (transcription?.enabled && (confSettings?.AUTO_CAPTION_ON_RECORDING ?? _autoCaptionOnRecord) && Boolean(language) === false) {

                if (confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1 ?? false) {
                    if (isLocalParticipantModerator(getState())) {
                        dispatch(showErrorNotification(
                    {
                        descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                        titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                    }));
                    } else {
                        return;
                    }
                } else {
                    dispatch(setRequestingSubtitles(true, Boolean(language), getState()['features/subtitles']._language));
                }
            }
        }

        if (updatedSessionData.status === ON
                && (!oldSessionData || oldSessionData.status !== ON) && !isRecorderButNotSipGateway && updatedSessionData.error !== 'error') {
            if (isStopping) {
                // Retry stop recording since Jibri is now ON
                const conf = getCurrentConference(getState());

                if (conf) {
                    logger.info(`Retrying stopRecording for session ${updatedSessionData.id} since it was marked as stopping`);
                    conf.stopRecording(updatedSessionData.id);
                }
            } else {
                if (typeof recordingLimit === 'object') {
                    // Show notification with additional information to the initiator.
                    dispatch(showRecordingLimitNotification(mode));
                } else {
                    dispatch(showStartedRecordingNotification(
                            mode, initiator && getParticipantDisplayName(getState, action.sessionData.id)));

                    // Show recording consent modal to guest participants (non-moderators)
                    // Use a small delay to ensure participant roles are properly set
                    const state = getState();
                    const participants = state['features/base/participants'];
                    const localParticipant = participants.find(p => p.local);

                    if (
                        !iAmRecorder
                        && localParticipant
                        && localParticipant.isRecordingStartedByUser === false
                        && !localParticipant.isShowConsentModal
                    ) {
                        dispatch(showRecordingConsentModal(localParticipant.id));
                    }
                }

                sendAnalytics(createRecordingEvent('start', mode));

                if (disableRecordAudioNotification) {
                    break;
                }

                let soundID;
                let soundID1;

                if (mode === MHRecordingConstants.mode.FILE && !isRecordingandLivestreamingOn) {
                    soundID = RECORDING_ON_SOUND_ID;
                    dispatch(setRecordingMode(mode));
                } else if (mode === MHRecordingConstants.mode.STREAM) {
                    soundID = LIVE_STREAMING_ON_SOUND_ID;
                    dispatch(setRecordingMode(mode));
                } else if (mode === MHRecordingConstants.mode.FILE && isRecordingandLivestreamingOn) {
                    soundID = RECORDING_ON_SOUND_ID;
                    soundID1 = LIVE_STREAMING_ON_SOUND_ID;
                    dispatch(setRecordingMode(mode));
                }

                if (soundID) {
                    dispatch(playSound(soundID));
                    if (soundID1) {
                        setTimeout(() => {
                            dispatch(playSound(soundID1));
                        }, 4000);
                    }
                }
            }


        } else if (updatedSessionData.status === OFF
                && (!oldSessionData || oldSessionData.status !== OFF)) {
            if (!isRecorderButNotSipGateway) {
                dispatch(
                        showStoppedRecordingNotification(
                            mode, getParticipantDisplayName(getState, getResourceId(terminator))));
            }

            // Reset the recording consent/started flags on the local participant so that
            // a subsequent recording session triggers the consent dialog fresh.
            const localParticipantOnStop = getState()['features/base/participants'].find(p => p.local);

            if (localParticipantOnStop) {
                dispatch(hideRecordingConsentModal(localParticipantOnStop.id));
                dispatch(resetRecordingStartedByUser(localParticipantOnStop.id));
            }

            if (isStopping) {
                dispatch({
                    type: RECORDING_STOP_CLEARED,
                    sessionId: updatedSessionData.id
                });

                if (isLocalParticipantModerator(getState())) {
                    conference && conference.removeCommand('recording-stop-requested');
                }
            }
            let duration = 0, soundOff, soundOff1, soundOn, soundon1;

            if (oldSessionData && oldSessionData.timestamp) {
                duration
                        = (Date.now() / 1000) - oldSessionData.timestamp;
            }
            sendAnalytics(createRecordingEvent('stop', mode, duration));

            if (disableRecordAudioNotification) {
                break;
            }

            let liveStreamDurationApiCall = false; // Use to send api call to portal for duration of livestreaming.

            if (mode === MHRecordingConstants.mode.FILE && !isRecordingandLivestreamingOn) {
                soundOff = RECORDING_OFF_SOUND_ID;
                soundOn = RECORDING_ON_SOUND_ID;
            } else if (mode === MHRecordingConstants.mode.STREAM) {
                soundOff = LIVE_STREAMING_OFF_SOUND_ID;
                soundOn = LIVE_STREAMING_ON_SOUND_ID;
                liveStreamDurationApiCall = true;
            } else if (mode === MHRecordingConstants.mode.FILE && isRecordingandLivestreamingOn) {
                soundOff = RECORDING_OFF_SOUND_ID;
                soundOn = RECORDING_ON_SOUND_ID;
                soundOff1 = LIVE_STREAMING_OFF_SOUND_ID;
                soundon1 = LIVE_STREAMING_ON_SOUND_ID;
                liveStreamDurationApiCall = true;
            }
            dispatch(recordingandLivestreamingOn(false));
            conference.sendCommand(IS_RECORDING_AND_LIVESTREAMING_ON, {
                value: false
            });

            if (soundOff && soundOn) {
                if (!isRecorderButNotSipGateway) {
                    dispatch(stopSound(soundOn));
                    dispatch(playSound(soundOff));
                }
                if (soundOff1 && soundon1) {

                    setTimeout(() => {
                        if (!isRecorderButNotSipGateway) {
                            dispatch(stopSound(soundon1));
                            dispatch(playSound(soundOff1));
                        }
                    }, 4000);
                }
                if (liveStreamDurationApiCall === true) {
                    const config = getState()['features/base/config'];
                    const MH_CLIENT_ID = config?.MH_CLIENT_ID;
                    const roomName = getBackendSafeRoomName(getState()['features/base/conference'].room);

                    _liveStreamingApiCall(MH_CLIENT_ID, roomName, duration, config).then(response => {
                        if (response.success === undefined || response?.success !== true) {
                            logger.error('Error in LiveStreamAPI for Duration failed');
                        }
                    });
                }
            }
        }
    }

        break;
    }

    return result;
});

/**
 * API CAll for LiveStreaming Duration update for Portal.
 *
 * @private
 * @param {string} clientid - Portal Client ID.
 * @param {string} roomName - Room Name.
 * @param {string} meetingDuration - LiveStreaming Duration.
 * @param {Object} config - Config from state.
 * @returns {bool | Object}
 */
async function _liveStreamingApiCall(clientid, roomName, meetingDuration, config) {
    const moement = moment().seconds(0)
        .milliseconds(0)
        .format('X');

    const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

    const x = meetingDuration;
    const int_part = Math.trunc(x);
    const float_part = Number((x - int_part).toFixed(2));

    const hours = Math.round((Math.floor(meetingDuration) / 60) / 60);
    const minutes = Math.round(meetingDuration / 60);
    const seconds = Math.round(float_part * 60);
    const milliseconds = Math.round(Math.floor(meetingDuration) / 1000);

    const finalDuration = `${hours}:${minutes}:${seconds}.${milliseconds}`;

    const referenceParam = {
        meeting_id: roomName,
        client_id: clientid,
        credentials: mesh256,
        duration: finalDuration,
        options: [ 'Livestream' ],
        ...isDomainWeborNative
    };

    const liveStreamAPIUpdate = await fetchAxiosApi(ULR_PATHS.LIVESTREAM_USAGE_UPDATE,
    {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': '*'
        },
        body: referenceParam,
        config
    }).catch(
        err => logger.error('Error in hitting LiveStreamingDurationAPI in api', err)
    );

    return liveStreamAPIUpdate ? liveStreamAPIUpdate : false;
}

/**
 * Shows a notification about an error in the recording session. A
 * default notification will display if no error is specified in the passed
 * in recording session.
 *
 * @private
 * @param {Object} recorderSession - The recorder session model from the
 * lib.
 * @param {Dispatch} dispatch - The Redux Dispatch function.
 * @param {NOTIFICATION_TIMEOUT_TYPE[0]} timeoutType - The type of timeout for the notification.
 * @returns {void}
 */
function _showRecordingErrorNotification(recorderSession, dispatch, timeoutType) {
    const isStreamMode
        = recorderSession.getMode()
            === MeetHourJS.constants.recording.mode.STREAM;

    switch (recorderSession.getError()) {
    case MeetHourJS.constants.recording.error.SERVICE_UNAVAILABLE:
        dispatch(showRecordingError({
            descriptionKey: 'recording.unavailable',
            descriptionArguments: {
                serviceName: isStreamMode
                    ? '$t(liveStreaming.serviceName)'
                    : '$t(recording.serviceName)'
            },
            titleKey: isStreamMode
                ? 'liveStreaming.unavailableTitle'
                : 'recording.unavailableTitle'
        }, timeoutType));
        break;
    case MeetHourJS.constants.recording.error.RESOURCE_CONSTRAINT:
        dispatch(showRecordingError({
            descriptionKey: isStreamMode
                ? 'liveStreaming.busy'
                : 'recording.busy',
            titleKey: isStreamMode
                ? 'liveStreaming.busyTitle'
                : 'recording.busyTitle'
        }, timeoutType));
        break;
    default:
        dispatch(showRecordingError({
            descriptionKey: isStreamMode
                ? 'liveStreaming.error'
                : 'recording.error',
            titleKey: isStreamMode
                ? 'liveStreaming.failedToStart'
                : 'recording.failedToStart'
        }, timeoutType));
        break;
    }
}
